import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ChevronLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: post?.meta_title || post?.title,
    description: post?.meta_description || post?.excerpt,
    keywords: post?.meta_keywords || undefined,
    image: post?.og_image || post?.cover_image,
    type: "article",
    breadcrumbs: post ? [
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: post.title, url: `/blog/${post.slug}` },
    ] : undefined,
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.cover_image,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: post.author },
    } : undefined,
  });

  if (loading) return <div className="container-page py-20 text-center text-slate-500">Loading…</div>;
  if (notFound || !post) return (
    <div className="container-page py-20 text-center">
      <h1 className="font-heading text-3xl font-semibold text-slate-900">Article not found</h1>
      <Link to="/blog" className="mt-6 inline-block"><Button>Back to blog</Button></Link>
    </div>
  );

  return (
    <article>
      <div className="border-b border-slate-200 bg-slate-50">
        <div className="container-page py-10">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary">
            <ChevronLeft className="h-4 w-4" /> All articles
          </Link>
          <h1 className="mt-4 font-heading text-3xl font-bold text-slate-900 sm:text-4xl">{post.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />{new Date(post.published_at).toLocaleDateString()}</span>
            <span>·</span>
            <span>{post.author}</span>
            {post.tags?.length > 0 && (
              <div className="ml-2 flex flex-wrap gap-1.5">
                {post.tags.map((t) => <Badge key={t} variant="secondary" className="bg-slate-200 text-slate-700">{t}</Badge>)}
              </div>
            )}
          </div>
        </div>
      </div>
      {post.cover_image && (
        <div className="container-page mt-8">
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <img src={post.cover_image} alt={post.title} className="aspect-[21/9] w-full object-cover" />
          </div>
        </div>
      )}
      <section className="container-page section-pad max-w-3xl">
        <div
          className="prose prose-slate max-w-none text-base leading-relaxed prose-headings:font-heading prose-headings:text-slate-900 prose-p:text-slate-700 prose-a:text-primary prose-strong:text-slate-900"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </section>
    </article>
  );
}
